/**
 * Utility function to construct proper logo URL from database path
 * Handles both full URLs, local paths, and MinIO storage keys
 * 
 * Path types:
 * - Full URLs (http/https): Return as-is
 * - Local paths (/uploads/, /shared/): Construct full backend URL
 * - MinIO storage keys (portal-logos/, documents/, etc.): Use proxy endpoint
 */
export const getLogoUrl = (logoPath: string | null | undefined): string | null => {
  // Handle null, undefined, or empty/whitespace strings
  if (!logoPath || (typeof logoPath === 'string' && !logoPath.trim())) {
    return null;
  }
  
  // At this point, logoPath is guaranteed to be a non-empty string
  // Trim whitespace for processing
  const trimmedPath = logoPath.trim();
  
  // If it's already a full URL (including presigned URLs), return as-is
  if (trimmedPath.startsWith('http://') || trimmedPath.startsWith('https://')) {
    return trimmedPath;
  }
  
  // Get API base URL
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
  const baseUrl = apiBaseUrl.replace('/api/v1', '');
  
  // If it's a relative path starting with /shared/, construct full URL
  if (trimmedPath.startsWith('/shared/')) {
    return `${baseUrl}${trimmedPath}`;
  }
  
  // If it starts with /uploads/ or uploads/, construct full URL
  if (trimmedPath.startsWith('/uploads/') || trimmedPath.startsWith('uploads/')) {
    const normalizedPath = trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
    return `${baseUrl}${normalizedPath}`;
  }
  
  // Check if it's a MinIO storage key (new format)
  const isStorageKey = !trimmedPath.startsWith('/') && 
                       !trimmedPath.startsWith('uploads/') &&
                       !trimmedPath.startsWith('http') &&
                       (trimmedPath.includes('/') || 
                        trimmedPath.startsWith('portal-logos/') ||
                        trimmedPath.startsWith('documents/') ||
                        trimmedPath.startsWith('shared/') ||
                        trimmedPath.startsWith('profile-picture/') ||
                        trimmedPath.startsWith('agent-registrations/') ||
                        trimmedPath.startsWith('agent-applications/') ||
                        trimmedPath.startsWith('blog/'));
  
  if (isStorageKey) {
    // For MinIO storage keys, use the proxy endpoint
    return `${apiBaseUrl}/admin/files/proxy?path=${encodeURIComponent(trimmedPath)}`;
  }
  
  // If it's a relative path that doesn't start with /, assume it's from root
  if (!trimmedPath.startsWith('/')) {
    return `${baseUrl}/${trimmedPath}`;
  }
  
  // Otherwise, return as-is
  return trimmedPath;
};

